import { useEffect, useMemo } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { isApiError } from '@/api/client'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { ErrorState, Skeleton } from '@/components/ui/StateMessage'

import { DrillDownProvider } from '../drilldown/DrillDownProvider'
import { usePeriodActions, usePeriodSelection } from '../hooks/usePeriod'
import { useRunwayQuery } from '../hooks/useRunway'
import { quarterCalendar } from '../lib/calendar'
import { pacePercent } from '../lib/pace'
import { RunwayContext, type RunwayModel } from '../RunwayContext'
import styles from './Layout.module.css'
import { PeriodBar } from './PeriodBar'
import { Sidebar } from './Sidebar'

export function AppLayout() {
  const { data: runway, isError, error } = useRunwayQuery()
  const { pathname } = useLocation()
  const { budgetId } = usePeriodSelection()
  const { setBudget } = usePeriodActions()

  // A stale `?budget=` (the budget was deleted in Sage) falls back to FPA's resolved budget.
  const unknownBudget = Boolean(budgetId) && isApiError(error) && error.status === 404
  useEffect(() => {
    if (unknownBudget) setBudget(null)
  }, [unknownBudget, setBudget])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [pathname])

  const model = useMemo<RunwayModel | null>(() => {
    if (!runway) return null
    return {
      runway,
      calendar: quarterCalendar(runway),
      pacePct: pacePercent(runway),
      deptName: (code: string) => runway.departments[code]?.name ?? code,
    }
  }, [runway])

  if (!model) {
    return (
      <div className={styles.centered}>
        {isError ? (
          <ErrorState>Could not load the runway. Check your connection and reload the page.</ErrorState>
        ) : (
          <Skeleton label="Loading department runway" tall />
        )}
      </div>
    )
  }

  return (
    <RunwayContext.Provider value={model}>
      <DrillDownProvider>
        <div className={styles.app}>
          <Sidebar />
          <main className={styles.main} id="main">
            <div className={styles.frame}>
              <ErrorBoundary resetKey={pathname}>
                <PeriodBar runway={model.runway} />
              </ErrorBoundary>
              <ErrorBoundary resetKey={pathname}>
                <Outlet />
              </ErrorBoundary>
            </div>
          </main>
        </div>
      </DrillDownProvider>
    </RunwayContext.Provider>
  )
}
